import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Briefcase,
  Building2,
  DollarSign,
  Pencil,
  Save,
  X,
  MapPin,
  Package,
  Coffee,
  Gift,
  CreditCard,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { OpportunityData } from "@/pages/npo/OpportunitiesRateCard";
import { PricingDetailsPanel } from "./PricingDetailsPanel";
import { LocationsList } from "./LocationsList";
import { BasePricingForm } from "./BasePricingForm";
import { GeographicMarkupSection } from "./GeographicMarkupSection";
import { AdditionalFeesForm } from "./AdditionalFeesForm";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle } from "lucide-react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Define the form schema for opportunity editing
const opportunityFormSchema = z.object({
  opportunity_id: z.string(),
  opportunity_name: z.string().min(1, "Opportunity name is required"),
  catalog_data: z.object({
    min_volunteers: z.string().or(z.number()),
    max_volunteers: z.string().or(z.number()),
    description: z.string().optional(),
    impact: z.string().optional(),
    format: z.string().min(1, "Event type is required"),
    duration: z.string().min(1, "Duration is required"),
  }),
  pricing_details: z.object({
    currency: z.string().min(1, "Currency is required"),
    cost_per_unit: z.string().or(z.number()),
    pricing_model: z.string(),
    enableGeographicPricing: z.boolean().optional(),
    geographicMarkups: z
      .array(
        z.object({
          country: z.string().min(1, "Country is required"),
          state: z.string().min(1, "State is required"),
          district: z.string().min(1, "City is required"),
          locationId: z.string(),
          locationName: z.string(),
          markupPercentage: z.string(),
        })
      )
      .optional(),
    additional_fees: z.object({
      program_management: z.object({
        amount: z.string().or(z.number()),
        fee_type: z.string(),
      }),
      material_kit: z.object({
        amount: z.string().or(z.number()),
        fee_type: z.string(),
      }),
      refreshments: z.object({
        amount: z.string().or(z.number()),
        fee_type: z.string(),
      }),
      goodies: z.object({
        amount: z.string().or(z.number()),
        fee_type: z.string(),
      }),
    }),
  }),
});

// Infer TypeScript type from the schema
type OpportunityFormValues = z.infer<typeof opportunityFormSchema>;

// Currency options
const currencies = [
  { label: "USD - US Dollar", value: "USD" },
  { label: "EUR - Euro", value: "EUR" },
  { label: "GBP - British Pound", value: "GBP" },
  { label: "JPY - Japanese Yen", value: "JPY" },
  { label: "CAD - Canadian Dollar", value: "CAD" },
  { label: "AUD - Australian Dollar", value: "AUD" },
  { label: "SGD - Singapore Dollar", value: "SGD" },
  { label: "INR - Indian Rupee", value: "INR" },
];

const eventTypes = [
  { label: "Indoor", value: "Indoor" },
  { label: "Outdoor", value: "Outdoor" },
  { label: "In-Office", value: "In-Office" },
];

const eventDurations = [
  { label: "30 mins", value: "30 mins" },
  { label: "60 mins", value: "60 mins" },
  { label: "90 mins", value: "90 mins" },
  { label: "120 mins", value: "120 mins" },
  { label: "180 mins", value: "180 mins" },
  { label: "Half day", value: "Half day" },
  { label: "Full day", value: "Full day" },
];

interface RateCardOpportunityCardProps {
  opportunity: OpportunityData;
  session: any;
  profile: any;
  onSave?: (
    opportunityId: string,
    data: OpportunityFormValues
  ) => Promise<void>;
  getDefaultFormValues: (opportunity: OpportunityData) => OpportunityFormValues;
}

export const RateCardOpportunityCard = ({
  opportunity,
  session,
  profile,
  onSave,
  getDefaultFormValues,
}: RateCardOpportunityCardProps) => {
  const [editMode, setEditMode] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{
    success: boolean;
    message: string;
    catalogId?: string;
  } | null>(null);

  // Load saved catalogId to skip recreate
  useEffect(() => {
    const stored = localStorage.getItem(
      `catalog_item_${opportunity.opportunity_id}`
    );
    if (stored) {
      setSaveResult({
        success: true,
        message: "Opportunity already submitted",
        catalogId: stored,
      });
    }
  }, [opportunity.opportunity_id]);

  // Create form instance
  const form = useForm<OpportunityFormValues>({
    resolver: zodResolver(opportunityFormSchema),
    defaultValues: getDefaultFormValues(opportunity),
  });
  const { control, watch } = form;

  // Toggle edit mode
  const toggleEditMode = () => {
    if (editMode) {
      // Reset form when canceling edit
      form.reset(getDefaultFormValues(opportunity));
    }
    setEditMode(!editMode);
    setSaveResult(null);
  };

  // Handle save
  const handleSave = async (data: OpportunityFormValues) => {
    if (!onSave) return;

    setIsSaving(true);
    setSaveResult(null);

    try {
      await onSave(opportunity.opportunity_id, data);
      setSaveResult({
        success: true,
        message: "Opportunity updated successfully!",
      });
      setEditMode(false);
    } catch (error) {
      console.error("Error saving opportunity:", error);
      setSaveResult({
        success: false,
        message: "Failed to update opportunity",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Submit to catalog endpoint
  const submitToCatalog = async (data: OpportunityFormValues) => {
    setIsSaving(true);
    setSaveResult(null);

    try {
      // Format data according to the required structure
      const formattedData = {
        id: data.opportunity_id,
        title: data.opportunity_name,
        description: data.catalog_data.description || "",
        prerequisites: opportunity.catalog_data.prerequisites || [],
        timeline: opportunity.catalog_data.timeline || [],
        format: data.catalog_data.format || "Outdoor",
        volunteer_output: data.catalog_data.impact || "",
        duration: data.catalog_data.duration,
        host_included: true,
        pricing: {
          currency: data.pricing_details.currency,
          cost_per_unit: Number(data.pricing_details.cost_per_unit),
          pricing_model: data.pricing_details.pricing_model,
          min_volunteers: data.catalog_data.min_volunteers,
          additional_fees: {
            goodies: {
              amount: Number(
                data.pricing_details.additional_fees.goodies.amount
              ),
              fee_type: data.pricing_details.additional_fees.goodies.fee_type,
              amount_usd: 0,
            },
            material_kit: {
              amount: Number(
                data.pricing_details.additional_fees.material_kit.amount
              ),
              fee_type:
                data.pricing_details.additional_fees.material_kit.fee_type,
              amount_usd: 0,
            },
            refreshments: {
              amount: Number(
                data.pricing_details.additional_fees.refreshments.amount
              ),
              fee_type:
                data.pricing_details.additional_fees.refreshments.fee_type,
              amount_usd: 0,
            },
            program_management: {
              amount: Number(
                data.pricing_details.additional_fees.program_management.amount
              ),
              fee_type:
                data.pricing_details.additional_fees.program_management
                  .fee_type,
              amount_usd: 0,
            },
          },
          cost_per_unit_usd: 0, // This would need to be calculated based on exchange rates
          additional_details: "", // Set a default empty string
          include_fees_in_base: true,
          max_volunteers: data.catalog_data.max_volunteers,
        },
        supported_locations: [
          ...opportunity.supported_locations,
          ...(data.pricing_details.geographicMarkups?.map(
            (m) => `${m.country}-${m.state}-${m.district}`
          ) ?? []),
        ],
      };

      // Make API call
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/catalog`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify(formattedData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit to catalog");
      }

      const result = await response.json();
      console.log("Catalog submission result:", result);

      setSaveResult({
        success: true,
        message: "Opportunity submitted to catalog successfully!",
        catalogId: result.data,
      });
      // Persist catalogId for future visits
      localStorage.setItem(
        `catalog_item_${opportunity.opportunity_id}`,
        result.data.toString()
      );
    } catch (error) {
      console.error("Error submitting to catalog:", error);
      setSaveResult({
        success: false,
        message:
          typeof error === "object" && error !== null && "message" in error
            ? String(error.message)
            : "Failed to submit opportunity to catalog",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="p-4 flex justify-between items-center border-b">
        <h2 className="text-xl font-semibold">
          {opportunity.opportunity_name}
        </h2>
        <div className="flex items-center gap-2">
          {saveResult && (
            <Alert
              className={`${
                saveResult.success ? "bg-green-50" : "bg-red-50"
              } flex items-center gap-1 p-2`}
            >
              {saveResult.success ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription
                className={
                  saveResult.success ? "text-green-600" : "text-red-600"
                }
              >
                {saveResult.message}
              </AlertDescription>
            </Alert>
          )}
          {saveResult?.success && saveResult?.catalogId ? (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              asChild
            >
              <a
                href={`/dashboard/catalog/${saveResult.catalogId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                View in Catalog
              </a>
            </Button>
          ) : (
            <Button
              type="button"
              disabled={isSaving}
              onClick={() => form.handleSubmit(submitToCatalog)()}
              className="flex items-center gap-1"
            >
              {isSaving ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin">◌</span>
                  Create
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" /> Create
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          {editMode ? (
            <CardContent className="p-6">
              {/* Edit mode content */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column: Opportunity details */}
                <div className="space-y-4">
                  <div className="relative h-48 rounded-md overflow-hidden">
                    {opportunity.image_url ? (
                      <img
                        src={opportunity.image_url}
                        alt={opportunity.opportunity_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <Briefcase className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Format badge */}
                  <div className="flex flex-wrap gap-2">
                    {opportunity.catalog_data.format && (
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                      >
                        <Building2 className="h-3.5 w-3.5 mr-1" />
                        {opportunity.catalog_data.format}
                      </Badge>
                    )}
                    {opportunity.similarity_score && (
                      <Badge
                        variant="secondary"
                        className="bg-green-100 text-green-800 hover:bg-green-200"
                      >
                        Match: {Math.round(opportunity.similarity_score * 100)}%
                      </Badge>
                    )}
                  </div>

                  {/* Currency Selection */}
                  <div className="mb-6 border-b pb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="h-5 w-5 text-primary" />
                      <h2 className="text-base font-semibold">
                        Currency Settings
                      </h2>
                    </div>
                    <FormField
                      control={control}
                      name="pricing_details.currency"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Currency</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select currency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {currencies.map((currency) => (
                                <SelectItem
                                  key={currency.value}
                                  value={currency.value}
                                >
                                  {currency.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Select the currency for all pricing in this
                            opportunity
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Event Type */}
                  <div className="mb-6 border-b pb-4">
                    <FormField
                      control={control}
                      name="catalog_data.format"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Event Type</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select event type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {eventTypes.map((evt) => (
                                <SelectItem key={evt.value} value={evt.value}>
                                  {evt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Duration */}
                  <div className="mb-6 border-b pb-4">
                    <FormField
                      control={control}
                      name="catalog_data.duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duration</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select duration" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {eventDurations.map((d) => (
                                <SelectItem key={d.value} value={d.value}>
                                  {d.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Base pricing form */}
                  <BasePricingForm
                    namePrefix="pricing_details"
                    format={opportunity.catalog_data.format}
                    currency={watch("pricing_details.currency")}
                  />
                </div>

                {/* Right column: Additional fees and geographic pricing */}
                <div className="space-y-4">
                  {/* Additional fees section */}
                  <AdditionalFeesForm
                    namePrefix="pricing_details"
                    currency={watch("pricing_details.currency")}
                  />

                  {/* Geographic markup section */}
                  <GeographicMarkupSection
                    namePrefix="pricing_details"
                    session={session}
                  />
                </div>
              </div>
            </CardContent>
          ) : (
            <CardContent className="p-6">
              {/* View mode content */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column: Opportunity details */}
                <div className="space-y-4">
                  <div className="relative h-48 rounded-md overflow-hidden">
                    {opportunity.image_url ? (
                      <img
                        src={opportunity.image_url}
                        alt={opportunity.opportunity_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <Briefcase className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <h3 className="text-xl font-bold text-white">
                        {opportunity.opportunity_name}
                      </h3>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {opportunity.catalog_data.format && (
                          <Badge
                            variant="secondary"
                            className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                          >
                            <Building2 className="h-3.5 w-3.5 mr-1" />
                            {opportunity.catalog_data.format}
                          </Badge>
                        )}
                        {opportunity.catalog_data.duration && (
                          <Badge
                            variant="secondary"
                            className="bg-blue-100 text-blue-800 hover:bg-blue-200"
                          >
                            Duration: {opportunity.catalog_data.duration}
                          </Badge>
                        )}
                        {opportunity.similarity_score && (
                          <Badge
                            variant="secondary"
                            className="bg-green-100 text-green-800 hover:bg-green-200"
                          >
                            Match:{" "}
                            {Math.round(opportunity.similarity_score * 100)}%
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {opportunity.catalog_data.description && (
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium mb-2">Description</h4>
                        <p className="text-sm text-gray-700">
                          {opportunity.catalog_data.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right column: Pricing details */}
                <div className="space-y-4">
                  <PricingDetailsPanel opportunity={opportunity} />

                  {/* Geographic pricing section (if enabled) */}
                  {opportunity.pricing_details.enableGeographicPricing &&
                    opportunity.pricing_details.geographicMarkups &&
                    opportunity.pricing_details.geographicMarkups.length >
                      0 && (
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-medium flex items-center gap-2 mb-3">
                          <MapPin className="h-4 w-4 text-blue-500" />
                          Geographic Pricing Adjustments
                        </h4>

                        <div className="space-y-3">
                          {opportunity.pricing_details.geographicMarkups.map(
                            (markup, index) => (
                              <div
                                key={index}
                                className="p-3 bg-blue-50 rounded-md"
                              >
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-blue-700">
                                    {markup.locationName}
                                  </span>
                                  <span className="font-medium text-blue-700">
                                    +{markup.markupPercentage}%
                                  </span>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  <LocationsList locations={opportunity.supported_locations} />
                </div>
              </div>
            </CardContent>
          )}
        </form>
      </FormProvider>
    </Card>
  );
};
